/**
 * SERP Preview Page
 *
 * Allows users to preview how a URL will appear in Google search results
 * and on social media platforms (Twitter/X, Facebook, LinkedIn).
 * Fetches meta tags from a URL and renders visual previews.
 */

import { useState, useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Monitor, Smartphone, Pencil, Loader2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/services/apiClient";

interface MetaData {
	url: string;
	title: string | null;
	meta_description: string | null;
	og_title: string | null;
	og_description: string | null;
	og_image: string | null;
	twitter_card: string | null;
	twitter_title: string | null;
	twitter_description: string | null;
	twitter_image: string | null;
	favicon: string | null;
}

// Google G Icon Component
function GoogleGIcon({ className = "" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			className={className}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
				fill="currentColor"
			/>
			<path
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
				fill="currentColor"
			/>
			<path
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
				fill="currentColor"
			/>
			<path
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
				fill="currentColor"
			/>
		</svg>
	);
}

// Character Counter Component
function CharCounter({
	current,
	max,
	placeholder,
}: {
	current: number;
	max: number;
	placeholder: string;
}) {
	return (
		<span className="text-xs text-muted-foreground">
			{current}/{max} · {placeholder}
		</span>
	);
}

// Pixel width measurement using canvas (matches Google's rendering)
let _measureCanvas: HTMLCanvasElement | null = null;
function measurePixelWidth(text: string, fontSize: number = 18, fontFamily: string = "Arial"): number {
	if (!text) return 0;
	if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
	const ctx = _measureCanvas.getContext("2d")!;
	ctx.font = `${fontSize}px ${fontFamily}`;
	return ctx.measureText(text).width;
}

// Find approximate character index where truncation occurs at a given pixel limit
function findTruncationIndex(text: string, maxPx: number, fontSize: number, fontFamily: string = "Arial"): number {
	if (!text) return 0;
	if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
	const ctx = _measureCanvas.getContext("2d")!;
	ctx.font = `${fontSize}px ${fontFamily}`;
	for (let i = 1; i <= text.length; i++) {
		if (ctx.measureText(text.slice(0, i)).width > maxPx) return i - 1;
	}
	return text.length;
}

// Google SERP pixel limits (desktop)
const TITLE_MAX_PX = 568;
const DESC_MAX_PX = 920;

// Pixel Width Bar Component
function PixelWidthBar({
	text,
	maxPx,
	fontSize,
	fontFamily = "Arial",
	label,
}: {
	text: string;
	maxPx: number;
	fontSize: number;
	fontFamily?: string;
	label: string;
}) {
	const currentPx = useMemo(() => Math.round(measurePixelWidth(text, fontSize, fontFamily)), [text, fontSize, fontFamily]);
	const pct = Math.min((currentPx / maxPx) * 100, 100);
	const overflowing = currentPx > maxPx;

	const barColor = (() => {
		const threshold = maxPx * 0.88; // ~88% = amber zone
		if (currentPx > maxPx) return "bg-red-500";
		if (currentPx > threshold) return "bg-amber-500";
		return "bg-emerald-500";
	})();

	const truncIdx = overflowing ? findTruncationIndex(text, maxPx, fontSize, fontFamily) : null;

	return (
		<div className="space-y-1">
			<div className="flex items-center justify-between">
				<span className="text-xs text-muted-foreground">
					{label}: {currentPx}px / {maxPx}px
				</span>
			</div>
			<div className="h-1.5 bg-muted rounded-full overflow-hidden">
				<div
					className={`h-full rounded-full transition-all duration-150 ${barColor}`}
					style={{ width: `${pct}%` }}
				/>
			</div>
			{overflowing && truncIdx !== null && (
				<div className="flex items-center gap-1.5 mt-1">
					<AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
					<span className="text-xs text-red-600">
						Will be truncated in Google results (approx. char {truncIdx})
					</span>
				</div>
			)}
		</div>
	);
}

// --- Preview components ---

function GoogleDesktopPreview({
	title,
	description,
	url,
	favicon,
}: {
	title: string;
	description: string;
	url: string;
	favicon: string | null;
}) {
	const displayUrl = url
		? (() => {
				try {
					const u = new URL(url);
					return u.hostname + (u.pathname !== "/" ? u.pathname : "");
				} catch {
					return url;
				}
			})()
		: "example.com";

	const siteName = url
		? (() => {
				try {
					const u = new URL(url);
					return u.hostname.replace(/^www\./, "");
				} catch {
					return "Site Name";
				}
			})()
		: "Site Name";

	return (
		<div className="max-w-[600px] font-sans">
			<div className="text-xs text-muted-foreground uppercase tracking-wider mb-4 text-center">
				Desktop Preview
			</div>
			{/* In dark mode, wrap the result in a light card so Google's fixed
			    hex colors stay authentic against the dark page. */}
			<div className="space-y-1 dark:rounded-lg dark:border dark:border-gray-200 dark:bg-white dark:p-3">
				<div className="flex items-center gap-2">
					<div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
						{favicon ? (
							<img
								src={favicon}
								alt=""
								className="w-5 h-5 object-contain"
								onError={(e) => {
									(e.target as HTMLImageElement).style.display = "none";
								}}
							/>
						) : (
							<GoogleGIcon className="w-5 h-5 text-gray-400" />
						)}
					</div>
					<div className="flex flex-col">
						<span className="text-sm text-[#202124] font-medium">{siteName}</span>
						<span className="text-xs text-[#5f6368]">{displayUrl}</span>
					</div>
				</div>
				<h3 className="text-xl text-[#1a0dab] leading-snug cursor-pointer hover:underline line-clamp-2 pt-1">
					{title || "Page Title"}
				</h3>
				<p className="text-sm text-[#4d5156] leading-relaxed line-clamp-2">
					{description || "Meta description will appear here..."}
				</p>
			</div>
		</div>
	);
}

function GoogleMobilePreview({
	title,
	description,
	url,
	favicon,
}: {
	title: string;
	description: string;
	url: string;
	favicon: string | null;
}) {
	const displayUrl = url
		? (() => {
				try {
					const u = new URL(url);
					return u.hostname;
				} catch {
					return url;
				}
			})()
		: "example.com";

	return (
		<div className="max-w-[360px] font-sans">
			<div className="text-xs text-muted-foreground uppercase tracking-wider mb-4 text-center">
				Mobile Preview
			</div>
			<div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
				<div className="flex items-center gap-2 text-xs text-[#202124]">
					{favicon && (
						<img
							src={favicon}
							alt=""
							className="w-5 h-5 rounded-full"
							onError={(e) => {
								(e.target as HTMLImageElement).style.display = "none";
							}}
						/>
					)}
					<span className="text-[#4d5156] text-xs">{displayUrl}</span>
				</div>
				<h3 className="text-base text-[#1a0dab] leading-snug cursor-pointer line-clamp-2">
					{title || "Page Title"}
				</h3>
				<p className="text-xs text-[#4d5156] leading-relaxed line-clamp-3">
					{description || "Meta description will appear here..."}
				</p>
			</div>
		</div>
	);
}

function TwitterPreview({
	title,
	description,
	image,
	url,
}: {
	title: string;
	description: string;
	image: string | null;
	url: string;
}) {
	const domain = url
		? (() => {
				try {
					return new URL(url).hostname;
				} catch {
					return url;
				}
			})()
		: "example.com";

	return (
		<div className="max-w-[500px]">
			<div className="text-xs text-muted-foreground uppercase tracking-wider mb-4 text-center">
				Twitter/X Preview
			</div>
			<div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
				{image && (
					<div className="h-[250px] bg-gray-100 overflow-hidden">
						<img
							src={image}
							alt=""
							className="w-full h-full object-cover"
							onError={(e) => {
								(e.target as HTMLImageElement).parentElement!.style.display =
									"none";
							}}
						/>
					</div>
				)}
				<div className="p-3 space-y-0.5">
					<div className="text-xs text-[#8899a6]">{domain}</div>
					<div className="text-sm font-semibold text-[#0f1419] line-clamp-1">
						{title || "Page Title"}
					</div>
					<div className="text-sm text-[#536471] line-clamp-2">
						{description || "Description will appear here..."}
					</div>
				</div>
			</div>
		</div>
	);
}

function FacebookPreview({
	title,
	description,
	image,
	url,
}: {
	title: string;
	description: string;
	image: string | null;
	url: string;
}) {
	const domain = url
		? (() => {
				try {
					return new URL(url).hostname.toUpperCase();
				} catch {
					return url.toUpperCase();
				}
			})()
		: "EXAMPLE.COM";

	return (
		<div className="max-w-[500px]">
			<div className="text-xs text-muted-foreground uppercase tracking-wider mb-4 text-center">
				Facebook Preview
			</div>
			<div className="border border-gray-200 rounded-md overflow-hidden bg-white">
				{image && (
					<div className="h-[260px] bg-gray-100 overflow-hidden">
						<img
							src={image}
							alt=""
							className="w-full h-full object-cover"
							onError={(e) => {
								(e.target as HTMLImageElement).parentElement!.style.display =
									"none";
							}}
						/>
					</div>
				)}
				<div className="p-3 bg-[#f2f3f5] space-y-1">
					<div className="text-xs text-[#606770] uppercase">{domain}</div>
					<div className="text-base font-semibold text-[#1d2129] line-clamp-2">
						{title || "Page Title"}
					</div>
					<div className="text-sm text-[#606770] line-clamp-1">
						{description || "Description will appear here..."}
					</div>
				</div>
			</div>
		</div>
	);
}

function LinkedInPreview({
	title,
	description,
	image,
	url,
}: {
	title: string;
	description: string;
	image: string | null;
	url: string;
}) {
	const domain = url
		? (() => {
				try {
					return new URL(url).hostname;
				} catch {
					return url;
				}
			})()
		: "example.com";

	return (
		<div className="max-w-[500px]">
			<div className="text-xs text-muted-foreground uppercase tracking-wider mb-4 text-center">
				LinkedIn Preview
			</div>
			<div className="border border-gray-200 rounded-md overflow-hidden bg-white">
				{image && (
					<div className="h-[250px] bg-gray-100 overflow-hidden">
						<img
							src={image}
							alt=""
							className="w-full h-full object-cover"
							onError={(e) => {
								(e.target as HTMLImageElement).parentElement!.style.display =
									"none";
							}}
						/>
					</div>
				)}
				<div className="p-3 space-y-1 bg-[#f3f2ef]">
					<div className="text-sm font-semibold text-[#000000e6] line-clamp-2">
						{title || "Page Title"}
					</div>
					<div className="text-xs text-[#00000099]">{domain}</div>
				</div>
			</div>
		</div>
	);
}

// Platform Icons
function TwitterXIcon({ className = "" }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
		</svg>
	);
}

function FacebookIcon({ className = "" }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
		</svg>
	);
}

function LinkedInIcon({ className = "" }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="currentColor">
			<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
		</svg>
	);
}

// --- Main Page ---

export default function SerpPreviewPage() {
	const [url, setUrl] = useState("");
	const [pageTitle, setPageTitle] = useState("");
	const [metaDescription, setMetaDescription] = useState("");
	const [ogImage, setOgImage] = useState<string | null>(null);
	const [favicon, setFavicon] = useState<string | null>(null);
	const [ogTitle, setOgTitle] = useState("");
	const [ogDescription, setOgDescription] = useState("");
	const [twitterTitle, setTwitterTitle] = useState("");
	const [twitterDescription, setTwitterDescription] = useState("");
	const [twitterImage, setTwitterImage] = useState<string | null>(null);

	const fetchMeta = useMutation({
		mutationFn: async (targetUrl: string) => {
			const { data } = await apiClient.get<MetaData>("/seo-tools/fetch-meta", {
				params: { url: targetUrl },
			});
			return data;
		},
		onSuccess: (data) => {
			setPageTitle(data.title || "");
			setMetaDescription(data.meta_description || "");
			setOgImage(data.og_image || null);
			setFavicon(data.favicon || null);
			setOgTitle(data.og_title || "");
			setOgDescription(data.og_description || "");
			setTwitterTitle(data.twitter_title || "");
			setTwitterDescription(data.twitter_description || "");
			setTwitterImage(data.twitter_image || null);
			toast.success("Meta tags fetched successfully");
		},
		onError: (err: any) => {
			const detail =
				err?.response?.data?.detail || err?.message || "Failed to fetch URL";
			toast.error(detail);
		},
	});

	const handleFetch = useCallback(() => {
		if (!url.trim()) {
			toast.error("Please enter a URL");
			return;
		}
		fetchMeta.mutate(url.trim());
	}, [url, fetchMeta]);

	// Resolved values for previews (use OG/Twitter overrides if available)
	const resolvedTitle = pageTitle || "Page Title";
	const resolvedDescription =
		metaDescription || "Meta description will appear here...";
	const resolvedOgTitle = ogTitle || resolvedTitle;
	const resolvedOgDescription = ogDescription || resolvedDescription;
	const resolvedTwitterTitle = twitterTitle || ogTitle || resolvedTitle;
	const resolvedTwitterDescription =
		twitterDescription || ogDescription || resolvedDescription;
	const resolvedTwitterImage = twitterImage || ogImage;

	return (
		<div className="space-y-6 max-w-7xl mx-auto">
			{/* Hero Section */}
			<div className="bg-card rounded-2xl p-8 border border-border">
				<div className="flex items-center gap-4">
					<div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
						<GoogleGIcon className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-3xl font-bold text-foreground">SERP Preview</h1>
						<p className="text-muted-foreground mt-1">
							See how your page appears in search results and social media
						</p>
					</div>
				</div>
			</div>

			{/* Auto-fetch from URL */}
			<div className="bg-gradient-to-br from-orange-50/50 to-amber-50/30 rounded-2xl p-6 border border-orange-100/50">
				<div className="flex items-center gap-2 mb-3">
					<Search className="w-5 h-5 text-orange-500" />
					<h2 className="text-lg font-semibold text-foreground">Auto-fetch from URL</h2>
				</div>
				<p className="text-muted-foreground mb-4 text-sm">
					Enter your website URL and we&apos;ll automatically extract the title, description, and social image.
				</p>
				<div className="flex gap-3">
					<Input
						placeholder="https://example.com"
						value={url}
						onChange={(e) => setUrl(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleFetch()}
						className="flex-1 bg-background border-border focus:border-orange-300 focus:ring-orange-200"
					/>
					<Button
						onClick={handleFetch}
						disabled={fetchMeta.isPending}
						className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-6"
					>
						{fetchMeta.isPending ? (
							<Loader2 className="w-4 h-4 animate-spin mr-2" />
						) : (
							<Search className="w-4 h-4 mr-2" />
						)}
						Fetch
					</Button>
				</div>
			</div>

			{/* Edit + Preview layout */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
				{/* Left: Edit Details */}
				<div className="space-y-6">
					<div className="flex items-center gap-2 text-foreground">
						<Pencil className="w-4 h-4" />
						<span className="font-medium">Edit Details</span>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label htmlFor="page-title" className="text-foreground">
								Page Title <span className="text-orange-500">*</span>
							</Label>
							<CharCounter
								current={pageTitle.length}
								max={60}
								placeholder="Enter a title"
							/>
						</div>
						<Input
							id="page-title"
							placeholder="Best SEO Tools for 2026 | Your Site Name"
							value={pageTitle}
							onChange={(e) => setPageTitle(e.target.value)}
							className="bg-background border-border focus:border-orange-300 focus:ring-orange-200"
						/>
						<PixelWidthBar
							text={pageTitle}
							maxPx={TITLE_MAX_PX}
							fontSize={20}
							label="Title pixel width"
						/>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label htmlFor="meta-desc" className="text-foreground">
								Meta Description <span className="text-orange-500">*</span>
							</Label>
							<CharCounter
								current={metaDescription.length}
								max={160}
								placeholder="Enter a description"
							/>
						</div>
						<Textarea
							id="meta-desc"
							placeholder="Discover the top SEO tools that will help you rank higher in 2026..."
							value={metaDescription}
							onChange={(e) => setMetaDescription(e.target.value)}
							rows={4}
							className="bg-background border-border focus:border-orange-300 focus:ring-orange-200 resize-none"
						/>
						<PixelWidthBar
							text={metaDescription}
							maxPx={DESC_MAX_PX}
							fontSize={14}
							label="Description pixel width"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="og-image" className="text-foreground">
							OG Image URL
						</Label>
						<Input
							id="og-image"
							placeholder="https://example.com/image.jpg"
							value={ogImage || ""}
							onChange={(e) => setOgImage(e.target.value || null)}
							className="bg-background border-border focus:border-orange-300 focus:ring-orange-200"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="og-title" className="text-foreground">
							OG Title (override)
						</Label>
						<Input
							id="og-title"
							placeholder="Defaults to page title"
							value={ogTitle}
							onChange={(e) => setOgTitle(e.target.value)}
							className="bg-background border-border focus:border-orange-300 focus:ring-orange-200"
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="og-desc" className="text-foreground">
							OG Description (override)
						</Label>
						<Textarea
							id="og-desc"
							placeholder="Defaults to meta description"
							value={ogDescription}
							onChange={(e) => setOgDescription(e.target.value)}
							rows={2}
							className="bg-background border-border focus:border-orange-300 focus:ring-orange-200 resize-none"
						/>
					</div>
				</div>

				{/* Right: Preview Tabs */}
				<div>
					<Tabs defaultValue="desktop" className="w-full">
						<TabsList className="w-full bg-transparent border-b border-border rounded-none p-0 h-auto flex justify-start gap-6">
							<TabsTrigger
								value="desktop"
								className="data-[state=active]:border-orange-500 data-[state=active]:text-foreground data-[state=active]:shadow-none border-b-2 border-transparent text-muted-foreground rounded-none px-1 py-3 text-sm font-medium flex items-center gap-2 hover:text-foreground transition-colors"
							>
								<Monitor className="w-4 h-4" />
								Desktop
							</TabsTrigger>
							<TabsTrigger
								value="mobile"
								className="data-[state=active]:border-orange-500 data-[state=active]:text-foreground data-[state=active]:shadow-none border-b-2 border-transparent text-muted-foreground rounded-none px-1 py-3 text-sm font-medium flex items-center gap-2 hover:text-foreground transition-colors"
							>
								<Smartphone className="w-4 h-4" />
								Mobile
							</TabsTrigger>
							<TabsTrigger
								value="twitter"
								className="data-[state=active]:border-orange-500 data-[state=active]:text-foreground data-[state=active]:shadow-none border-b-2 border-transparent text-muted-foreground rounded-none px-1 py-3 text-sm font-medium flex items-center gap-2 hover:text-foreground transition-colors"
							>
								<TwitterXIcon className="w-4 h-4" />
								Twitter/X
							</TabsTrigger>
							<TabsTrigger
								value="facebook"
								className="data-[state=active]:border-orange-500 data-[state=active]:text-foreground data-[state=active]:shadow-none border-b-2 border-transparent text-muted-foreground rounded-none px-1 py-3 text-sm font-medium flex items-center gap-2 hover:text-foreground transition-colors"
							>
								<FacebookIcon className="w-4 h-4" />
								Facebook
							</TabsTrigger>
							<TabsTrigger
								value="linkedin"
								className="data-[state=active]:border-orange-500 data-[state=active]:text-foreground data-[state=active]:shadow-none border-b-2 border-transparent text-muted-foreground rounded-none px-1 py-3 text-sm font-medium flex items-center gap-2 hover:text-foreground transition-colors"
							>
								<LinkedInIcon className="w-4 h-4" />
								LinkedIn
							</TabsTrigger>
						</TabsList>

						<TabsContent value="desktop" className="mt-6">
							<div className="bg-muted rounded-xl p-8 border border-border">
								<GoogleDesktopPreview
									title={resolvedTitle}
									description={resolvedDescription}
									url={url}
									favicon={favicon}
								/>
							</div>
						</TabsContent>

						<TabsContent value="mobile" className="mt-6">
							<div className="bg-muted rounded-xl p-8 border border-border">
								<GoogleMobilePreview
									title={resolvedTitle}
									description={resolvedDescription}
									url={url}
									favicon={favicon}
								/>
							</div>
						</TabsContent>

						<TabsContent value="twitter" className="mt-6">
							<div className="bg-muted rounded-xl p-8 border border-border">
								<TwitterPreview
									title={resolvedTwitterTitle}
									description={resolvedTwitterDescription}
									image={resolvedTwitterImage}
									url={url}
								/>
							</div>
						</TabsContent>

						<TabsContent value="facebook" className="mt-6">
							<div className="bg-muted rounded-xl p-8 border border-border">
								<FacebookPreview
									title={resolvedOgTitle}
									description={resolvedOgDescription}
									image={ogImage}
									url={url}
								/>
							</div>
						</TabsContent>

						<TabsContent value="linkedin" className="mt-6">
							<div className="bg-muted rounded-xl p-8 border border-border">
								<LinkedInPreview
									title={resolvedOgTitle}
									description={resolvedOgDescription}
									image={ogImage}
									url={url}
								/>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</div>
	);
}
